'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Canonical education-level badge — the single surface for showing a course
 * level anywhere (course cards, course-detail hero/title, grids…).
 *
 * <p>Background comes from the site-builder theme tokens
 * (`color.courseLevel*` → `--color-courseLevel*` CSS vars) so the level
 * colors are configured once in the theme editor and stay consistent across
 * every block and theme. An rgba fallback keeps it styled before a tenant
 * customises the theme.
 *
 * <p>{@code level} may be a code (`BEGINNER`/`INTERMEDIATE`/`ADVANCED`/
 * `PUBLIC`) or a display string ("Beginner", "İleri Seviye"); it is
 * normalised by keyword to pick the color. The label is the explicit
 * {@code label} when given, else the translated level name when the input is
 * a known code, else the raw {@code level} string.
 *
 * <p>A course with no level restriction is a general/public course, and the
 * backend simply omits the field for those — so a missing {@code level} falls
 * back to {@code PUBLIC} ("Genel Seviye") instead of rendering nothing. Pass
 * {@code fallbackLevel={null}} to keep the old "render nothing" behaviour.
 */
export type LevelCode = 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'PUBLIC';

const LEVEL_COLOR: Record<LevelCode, string> = {
  BEGINNER: 'var(--color-courseLevelBeginner, rgba(56, 147, 69, 0.6))',
  INTERMEDIATE: 'var(--color-courseLevelIntermediate, rgba(239, 127, 51, 0.6))',
  ADVANCED: 'var(--color-courseLevelAdvanced, rgba(160, 29, 26, 0.6))',
  PUBLIC: 'var(--color-courseLevelPublic, rgba(27, 108, 175, 0.6))',
};

/** Solid, high-contrast level colors — used by the `solid` banner variant
 *  (white text on a saturated fill, as on the course-card cover banner).
 *  The pill tints above are the same hues at 60% alpha.
 *  Beginner/Intermediate are darkened from the original #389345/#ef7f33 —
 *  those measured ~3.9:1 and ~2.7:1 against white text, both under the
 *  4.5:1 WCAG AA minimum for 14px bold. These land at ~5:1+. */
const LEVEL_SOLID: Record<LevelCode, string> = {
  BEGINNER: 'var(--color-courseLevelBeginnerSolid, #217a34)',
  INTERMEDIATE: 'var(--color-courseLevelIntermediateSolid, #c2410c)',
  ADVANCED: 'var(--color-courseLevelAdvancedSolid, #a01d1a)',
  PUBLIC: 'var(--color-courseLevelPublicSolid, #1b6caf)',
};

const LEVEL_I18N: Record<LevelCode, string> = {
  BEGINNER: 'common:label.learning-level-beginner',
  INTERMEDIATE: 'common:label.learning-level-intermediate',
  ADVANCED: 'common:label.learning-level-advanced',
  PUBLIC: 'common:label.learning-level-public',
};

/** Map a level code OR a display string (EN/TR) to a canonical level code. */
export function normalizeLevelCode(level: string | undefined | null): LevelCode | null {
  if (!level) return null;
  const l = level.trim().toLowerCase();
  if (!l) return null;
  if (l.includes('advanced') || l.includes('expert') || l.includes('ileri')) return 'ADVANCED';
  if (l.includes('intermediate') || l.includes('inter') || l.includes('orta'))
    return 'INTERMEDIATE';
  if (l.includes('public') || l.includes('general') || l.includes('genel')) return 'PUBLIC';
  if (
    l.includes('beginner') ||
    l.includes('basic') ||
    l.includes('temel') ||
    l.includes('başlangıç') ||
    l.includes('baslangic')
  ) {
    return 'BEGINNER';
  }
  return null;
}

const KNOWN_CODE = /^(beginner|intermediate|advanced|public)$/i;

const baseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  // Hug the text — never stretch to fill a flex/grid parent's cross axis.
  alignSelf: 'flex-start',
  width: 'fit-content',
  maxWidth: '100%',
  padding: 'var(--space-1, 4px) var(--space-2, 8px)',
  borderRadius: 'var(--radius-full, 999px)',
  fontSize: 'var(--font-size-xs, 12px)',
  fontWeight: 600,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  // Level tints are light by design → dark text reads on every theme.
  color: '#1f2937',
};

const solidStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  // Hug the text — never stretch to fill a flex/grid parent's cross axis.
  alignSelf: 'flex-start',
  width: 'fit-content',
  maxWidth: '100%',
  padding: 'var(--space-1-5, 8px) var(--space-4, 16px)',
  borderRadius: 0,
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 700,
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  // Saturated fills are dark by design → white text reads on every level.
  color: '#fff',
};

/** Level assumed when the backend sends no level at all (general course). */
const DEFAULT_LEVEL: LevelCode = 'PUBLIC';

export interface LevelBadgeProps {
  /** Level code or display string. Empty → {@link LevelBadgeProps.fallbackLevel}. */
  level: string | undefined | null;
  /** Level used when {@code level} is empty. Defaults to `PUBLIC` (general);
   *  pass `null` to render nothing instead. */
  fallbackLevel?: LevelCode | null;
  /** Explicit label override; falls back to the translated/raw level. */
  label?: string;
  /** `pill` (default) = light tint + dark text, rounded.
   *  `solid` = saturated fill + white text, square (course-card banner). */
  variant?: 'pill' | 'solid';
  className?: string;
  style?: CSSProperties;
}

export function LevelBadge({
  level,
  label,
  fallbackLevel = DEFAULT_LEVEL,
  variant = 'pill',
  className,
  style,
}: LevelBadgeProps) {
  const { t } = useTranslation('common');
  const raw = (level ?? '').trim();
  if (!raw && !label && !fallbackLevel) return null;

  // No level from the backend → treat the course as general/public.
  const code = raw ? normalizeLevelCode(raw) : fallbackLevel;
  const solid = variant === 'solid';
  const bg = solid
    ? code
      ? LEVEL_SOLID[code]
      : 'var(--color-neutral-700, #404040)'
    : code
      ? LEVEL_COLOR[code]
      : 'var(--color-neutral-100, #f4f4f5)';

  const text =
    label?.trim() || (code && (!raw || KNOWN_CODE.test(raw)) ? t(LEVEL_I18N[code]) : raw);
  if (!text) return null;

  return (
    <span
      className={className}
      style={{ ...(solid ? solidStyle : baseStyle), background: bg, ...style }}
    >
      {text}
    </span>
  );
}
